import React, { useEffect, useState } from 'react';
import {
    Mail,
    Phone,
    CreditCard,
    Clock,
    Calendar,
    LogIn,
    CheckCircle2,
    Lock,
    Smartphone,
    History,
    Loader2,
    AlertCircle,
} from "lucide-react";
import moment from 'moment';
import { useAuth } from '../../services/contexts/authContext';
import { convertToPresentableText, Notify } from '../../utils/functions';
import { ChangeUserPassword } from '../../services/firebase/auth';
import { useMarkPasswordChanged } from '../../custom-hooks/use-users';

// Mock function to simulate fetching user data
const useGetUserProfile = (userId) => {
    const { user, userDetails } = useAuth();
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        // Simulate API call
        setUserData({
            _id: userDetails._id,
            name: userDetails.name,
            email: userDetails.email,
            phone: userDetails.phone,
            photoURL: user.photoURL,
            cnic: userDetails.cnic,
            jobTitle: userDetails.jobTitle,
            role: userDetails.role,
            isActive: userDetails.isActive,
            lastLogin: userDetails.lastLogin,
            createdDate: userDetails.createdDate,
            address: userDetails.address,
            meetingRoomQuota: userDetails.meetingRoomQuota,
        });
    }, [user, userDetails]);

    return { userData, isLoading: false };
};

// Helper function to format date with time
const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('MMM DD, YYYY, h:mm A');
};

const SectionCard = ({ title, children }) => (
    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-wider text-[#8C8175]">{title}</h2>
        {children}
    </section>
);

const InfoRow = ({ icon, label, value, valueClassName = 'text-[#8C8175]' }) => (
    <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cream-input text-brand">
            {icon}
        </div>
        <div className="min-w-0">
            <div className="text-sm font-semibold text-ink">{label}</div>
            <div className={`break-words text-sm ${valueClassName}`}>{value}</div>
        </div>
    </div>
);

const PasswordField = ({ label, value, onChange }) => (
    <div>
        <label className="mb-1.5 block text-[12.5px] font-semibold text-[#3B2A1E]">{label}</label>
        <input
            type="password"
            value={value}
            onChange={onChange}
            className="h-11 w-full rounded-xl border-[1.5px] border-cream-200 bg-white px-3.5 text-sm text-[#2B2420] outline-none transition-colors focus:border-brand"
        />
    </div>
);

const UserProfile = ({ userId = "user123" }) => {
    const { userData, isLoading } = useGetUserProfile(userId);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const markPasswordChangedMutation = useMarkPasswordChanged();

    const resetPasswordForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
    };

    const handleUpdatePassword = async () => {
        setPasswordError('');

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError('Please fill all password fields.');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('Confirm password does not match.');
            return;
        }

        if (currentPassword === newPassword) {
            setPasswordError('New password must be different from the current password.');
            return;
        }

        try {
            setPasswordLoading(true);
            await ChangeUserPassword({ currentPassword, newPassword });
            try {
                await markPasswordChangedMutation.mutateAsync();
            } catch (backendError) {
                console.error("Backend acknowledgment failed", backendError);
            }
            Notify('Password updated successfully', 'success');
            resetPasswordForm();
            setShowPasswordForm(false);
        } catch (error) {
            if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
                setPasswordError('Current password is incorrect.');
                Notify('Current password is incorrect.', 'error');
            } else if (error?.code === 'auth/too-many-requests') {
                setPasswordError('Too many attempts. Please try again later.');
                Notify('Too many attempts. Please try again later.', 'error');
            } else {
                setPasswordError('Failed to update password. Please try again.');
                Notify('Unable to update password right now. Please try again.', 'error');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand" />
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="px-6 py-10 text-center text-[#8C8175]">User not found</div>
        );
    }

    const presentableName = convertToPresentableText(userData.name || '');
    const avatarInitial = (presentableName || 'U').trim().charAt(0).toUpperCase();
    const subtitle = userData.jobTitle || convertToPresentableText(userData.role || '');

    const monthly = userData.meetingRoomQuota?.monthlyHours || 0;
    const used = userData.meetingRoomQuota?.usedHours || 0;
    const usedPct = monthly ? parseInt((used / monthly) * 100) : 0;
    const remainingPct = 100 - usedPct;

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header card */}
            <div className="overflow-hidden rounded-[18px] border border-[#ECE3D5] bg-white">
                <div
                    className="relative h-[130px]"
                    style={{ background: 'linear-gradient(115deg, #C9651B 0%, #9A480E 55%, #4A2E1A 100%)' }}
                >
                    <div className="pointer-events-none absolute -right-[30px] -top-10 h-[220px] w-[220px] rounded-full border border-white/[0.14]" />
                    <div className="pointer-events-none absolute -bottom-[60px] right-[60px] h-40 w-40 rounded-full border border-white/10" />
                </div>

                <div className="px-[26px] pb-[22px]">
                    {userData.photoURL ? (
                        <img
                            src={userData.photoURL}
                            alt={presentableName}
                            className="relative z-10 -mt-[52px] h-24 w-24 rounded-3xl border-4 border-white object-cover shadow-sm"
                        />
                    ) : (
                        <div className="relative z-10 -mt-[52px] flex h-24 w-24 items-center justify-center rounded-3xl border-4 border-white bg-cream-input font-display text-4xl font-bold text-brand shadow-sm">
                            {avatarInitial}
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-3">
                        <h1 className="font-display text-2xl font-bold text-ink">{presentableName}</h1>
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${userData.isActive ? 'bg-green-100 text-green-700' : 'bg-cream-100 text-[#6B675F]'}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${userData.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                            {userData.isActive ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    {subtitle && <p className="mt-1 text-sm text-[#8C8175]">{subtitle}</p>}
                </div>
            </div>

            {/* Details grid */}
            <div className="mt-[18px] grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                {/* Contact Information */}
                <SectionCard title="Contact Information">
                    <div className="space-y-4">
                        <InfoRow icon={<Mail size={18} />} label="Email Address" value={userData.email} />
                        <InfoRow icon={<Phone size={18} />} label="Phone Number" value={userData.phone} />
                        <InfoRow icon={<CreditCard size={18} />} label="CNIC" value={userData.cnic} />
                    </div>
                </SectionCard>

                {/* Meeting Room Bookings Quota */}
                <SectionCard title="Meeting Room Bookings Quota">
                    <InfoRow icon={<Clock size={18} />} label="Total Monthly Hours" value={`${monthly / 60} hours`} />
                    <div className="mt-5 space-y-4">
                        <div>
                            <div className="mb-1.5 flex items-center justify-between text-sm">
                                <span className="text-[#6B675F]">Used · {used / 60} hours</span>
                                <span className="font-semibold text-brand">{usedPct}% used</span>
                            </div>
                            <div className="h-2.5 w-full overflow-hidden rounded-full bg-cream-100">
                                <div className="h-full rounded-full bg-brand" style={{ width: `${usedPct}%` }} />
                            </div>
                        </div>
                        <div>
                            <div className="mb-1.5 flex items-center justify-between text-sm">
                                <span className="text-[#6B675F]">Remaining · {(monthly - used) / 60} hours</span>
                                <span className="font-semibold text-green-600">{remainingPct}% left</span>
                            </div>
                            <div className="h-2.5 w-full overflow-hidden rounded-full bg-cream-100">
                                <div className="h-full rounded-full bg-green-500" style={{ width: `${remainingPct}%` }} />
                            </div>
                        </div>
                    </div>
                </SectionCard>

                {/* Account Information */}
                <SectionCard title="Account Information">
                    <div className="space-y-4">
                        <InfoRow icon={<Calendar size={18} />} label="Account Created" value={formatDateTime(userData.createdDate)} />
                        <InfoRow icon={<LogIn size={18} />} label="Last Login" value={formatDateTime(userData.lastLogin)} />
                        <InfoRow
                            icon={<CheckCircle2 size={18} />}
                            label="Account Status"
                            value={userData.isActive ? 'Active' : 'Inactive'}
                            valueClassName={userData.isActive ? 'text-green-600 font-medium' : 'text-[#8C8175]'}
                        />
                    </div>
                </SectionCard>

                {/* Security Settings */}
                <SectionCard title="Security Settings">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cream-input text-brand">
                                    <Lock size={18} />
                                </div>
                                <span className="text-sm font-medium text-ink">Change Password</span>
                            </div>
                            <button
                                onClick={() => {
                                    setShowPasswordForm(prev => !prev);
                                    setPasswordError('');
                                }}
                                className="shrink-0 rounded-lg border border-[#ECE3D5] px-4 py-1.5 text-sm font-semibold text-ink transition-colors hover:bg-cream-input"
                            >
                                {showPasswordForm ? 'Cancel' : 'Change'}
                            </button>
                        </div>

                        {showPasswordForm && (
                            <div className="space-y-3 rounded-xl border border-[#ECE3D5] bg-cream p-4">
                                {passwordError && (
                                    <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                                        <span>{passwordError}</span>
                                    </div>
                                )}

                                <PasswordField label="Current Password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                                <PasswordField label="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                <PasswordField label="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />

                                <div className="flex justify-end">
                                    <button
                                        onClick={handleUpdatePassword}
                                        disabled={passwordLoading}
                                        className="flex items-center justify-center gap-2 rounded-xl bg-brand px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-70"
                                    >
                                        {passwordLoading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                <span>Updating...</span>
                                            </>
                                        ) : 'Save Password'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cream-input text-brand">
                                    <Smartphone size={18} />
                                </div>
                                <span className="text-sm font-medium text-ink">Two-Factor Authentication</span>
                            </div>
                            <button
                                disabled
                                className="shrink-0 cursor-not-allowed rounded-lg border border-[#ECE3D5] px-4 py-1.5 text-sm font-semibold text-[#B7AC9E]"
                            >
                                Enable
                            </button>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cream-input text-brand">
                                    <History size={18} />
                                </div>
                                <span className="text-sm font-medium text-ink">Login Activity Log</span>
                            </div>
                            <button
                                disabled
                                className="shrink-0 cursor-not-allowed rounded-lg border border-[#ECE3D5] px-4 py-1.5 text-sm font-semibold text-[#B7AC9E]"
                            >
                                View
                            </button>
                        </div>
                    </div>
                </SectionCard>
            </div>
        </div>
    );
};

export default UserProfile;
